import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Plane, MessageSquare, Calendar, Mail, Phone, MapPin,
  Clock, CheckCircle, XCircle, Eye, ChevronDown, ChevronUp,
  RefreshCw, User, DollarSign
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status badge component
const StatusBadge = ({ status, type }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'contacted':
        return 'bg-yellow-100 text-yellow-800';
      case 'booked':
      case 'paid':
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending_payment':
        return 'bg-orange-100 text-orange-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor()}`}>
      {status.replace('_', ' ').toUpperCase()}
    </span>
  );
};

// Expandable card for inquiries/consultations
const ExpandableCard = ({ item, type, onStatusUpdate }) => {
  const [expanded, setExpanded] = useState(false);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-navy/10 rounded-full flex items-center justify-center">
              {type === 'inquiry' ? (
                <Plane className="w-5 h-5 text-navy" />
              ) : type === 'consultation' ? (
                <Calendar className="w-5 h-5 text-navy" />
              ) : (
                <MessageSquare className="w-5 h-5 text-navy" />
              )}
            </div>
            <div>
              <p className="font-semibold text-navy">{item.name}</p>
              <p className="text-sm text-gray-500">{item.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={item.status || 'new'} type={type} />
            {type === 'consultation' && item.paid && (
              <span className="flex items-center gap-1 text-green-600 text-sm">
                <DollarSign className="w-4 h-4" />
                Paid
              </span>
            )}
            <span className="text-xs text-gray-400">{formatDate(item.created_at)}</span>
            {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-4">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Contact Info */}
            <div className="space-y-2">
              <h4 className="font-medium text-navy text-sm">Contact Information</h4>
              {item.phone && (
                <p className="text-sm flex items-center gap-2 text-gray-600">
                  <Phone className="w-4 h-4 text-gold" />
                  {item.phone}
                </p>
              )}
              <p className="text-sm flex items-center gap-2 text-gray-600">
                <Mail className="w-4 h-4 text-gold" />
                <a href={`mailto:${item.email}`} className="text-navy hover:underline">{item.email}</a>
              </p>
            </div>

            {/* Details based on type */}
            {type === 'inquiry' && (
              <div className="space-y-2">
                <h4 className="font-medium text-navy text-sm">Trip Details</h4>
                {item.destination && (
                  <p className="text-sm flex items-center gap-2 text-gray-600">
                    <MapPin className="w-4 h-4 text-gold" />
                    Destination: {item.destination}
                  </p>
                )}
                {item.travel_date && (
                  <p className="text-sm flex items-center gap-2 text-gray-600">
                    <Calendar className="w-4 h-4 text-gold" />
                    Travel Date: {item.travel_date}
                  </p>
                )}
                {item.adults && (
                  <p className="text-sm text-gray-600">
                    Travelers: {item.adults} adults{item.children && item.children !== '0' ? `, ${item.children} children` : ''}
                  </p>
                )}
                {item.budget && (
                  <p className="text-sm text-gray-600">Budget: {item.budget}</p>
                )}
                {item.special_occasion && (
                  <p className="text-sm text-gray-600">Occasion: {item.special_occasion}</p>
                )}
              </div>
            )}

            {type === 'consultation' && (
              <div className="space-y-2">
                <h4 className="font-medium text-navy text-sm">Consultation Details</h4>
                {item.preferred_date && (
                  <p className="text-sm flex items-center gap-2 text-gray-600">
                    <Calendar className="w-4 h-4 text-gold" />
                    Preferred: {item.preferred_date} {item.preferred_time && `(${item.preferred_time})`}
                  </p>
                )}
                {item.travel_interests && (
                  <div>
                    <p className="text-sm font-medium text-gray-600">Travel Interests:</p>
                    <p className="text-sm text-gray-600 mt-1">{item.travel_interests}</p>
                  </div>
                )}
              </div>
            )}

            {type === 'message' && (
              <div className="space-y-2">
                <h4 className="font-medium text-navy text-sm">Message</h4>
                <p className="text-sm text-gray-600">Subject: {item.subject}</p>
              </div>
            )}
          </div>

          {/* Additional Details */}
          {(item.additional_details || item.cruise_preference || item.notes || item.message) && (
            <div className="mt-4 p-3 bg-cream rounded-lg">
              <h4 className="font-medium text-navy text-sm mb-2">
                {type === 'message' ? 'Message' : 'Additional Notes'}
              </h4>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">
                {item.additional_details || item.cruise_preference || item.notes || item.message}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="mt-4 flex flex-wrap gap-2">
            {type !== 'message' && item.status === 'new' && (
              <Button
                size="sm"
                onClick={() => onStatusUpdate(item, 'contacted')}
                className="bg-yellow-500 hover:bg-yellow-600 text-white"
              >
                <Phone className="w-4 h-4 mr-1" />
                Mark Contacted
              </Button>
            )}
            {type === 'inquiry' && item.status === 'contacted' && (
              <Button
                size="sm"
                onClick={() => onStatusUpdate(item, 'booked')}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Mark Booked
              </Button>
            )}
            {type === 'consultation' && item.status !== 'completed' && item.paid && (
              <Button
                size="sm"
                onClick={() => onStatusUpdate(item, 'completed')}
                className="bg-green-500 hover:bg-green-600 text-white"
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Mark Completed
              </Button>
            )}
            <a href={`mailto:${item.email}`}>
              <Button size="sm" variant="outline" className="border-navy text-navy">
                <Mail className="w-4 h-4 mr-1" />
                Send Email
              </Button>
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Travel Admin Tab Component
export const TravelInquiriesTab = () => {
  const [inquiries, setInquiries] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSubTab, setActiveSubTab] = useState('inquiries');
  const [filter, setFilter] = useState('all');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [inquiriesRes, consultationsRes, messagesRes] = await Promise.all([
        axios.get(`${API}/travel-inquiries`),
        axios.get(`${API}/consultations`),
        axios.get(`${API}/contact-messages`)
      ]);
      setInquiries(inquiriesRes.data);
      setConsultations(consultationsRes.data);
      setMessages(messagesRes.data);
    } catch (error) {
      toast.error('Failed to fetch travel data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleStatusUpdate = async (item, newStatus, type) => {
    try {
      const endpoint = type === 'inquiry' 
        ? `/travel-inquiries/${item.id || item._id}/status`
        : type === 'consultation'
        ? `/consultations/${item.id || item._id}/status`
        : `/contact-messages/${item.id || item._id}/status`;
      
      // For now, update locally (backend routes for status update can be added later)
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getFilteredData = (data) => {
    if (filter === 'all') return data;
    return data.filter(item => item.status === filter);
  };

  const counts = {
    inquiries: inquiries.length,
    consultations: consultations.length,
    messages: messages.length,
    newInquiries: inquiries.filter(i => i.status === 'new').length,
    paidConsultations: consultations.filter(c => c.paid).length
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Plane className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-navy">{counts.inquiries}</p>
              <p className="text-xs text-gray-500">Travel Inquiries</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-navy">{counts.consultations}</p>
              <p className="text-xs text-gray-500">Consultations</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gold/20 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-gold" />
            </div>
            <div>
              <p className="text-2xl font-bold text-navy">${counts.paidConsultations * 25}</p>
              <p className="text-xs text-gray-500">Consultation Revenue</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-navy">{counts.messages}</p>
              <p className="text-xs text-gray-500">Contact Messages</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sub-tabs and Refresh */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-2">
          {[
            { id: 'inquiries', label: 'Travel Inquiries', count: counts.inquiries },
            { id: 'consultations', label: 'Consultations', count: counts.consultations },
            { id: 'messages', label: 'Contact Messages', count: counts.messages }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeSubTab === tab.id
                  ? 'bg-navy text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchData}
          className="flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {['all', 'new', 'contacted', 'booked', 'paid', 'completed'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
              filter === f
                ? 'bg-gold text-navy'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="space-y-4">
        {activeSubTab === 'inquiries' && (
          getFilteredData(inquiries).length > 0 ? (
            getFilteredData(inquiries).map((inquiry, index) => (
              <ExpandableCard 
                key={index} 
                item={inquiry} 
                type="inquiry"
                onStatusUpdate={(item, status) => handleStatusUpdate(item, status, 'inquiry')}
              />
            ))
          ) : (
            <div className="text-center py-12 bg-white rounded-xl">
              <Plane className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No travel inquiries found</p>
            </div>
          )
        )}

        {activeSubTab === 'consultations' && (
          getFilteredData(consultations).length > 0 ? (
            getFilteredData(consultations).map((consultation, index) => (
              <ExpandableCard 
                key={index} 
                item={consultation} 
                type="consultation"
                onStatusUpdate={(item, status) => handleStatusUpdate(item, status, 'consultation')}
              />
            ))
          ) : (
            <div className="text-center py-12 bg-white rounded-xl">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No consultations found</p>
            </div>
          )
        )}

        {activeSubTab === 'messages' && (
          getFilteredData(messages).length > 0 ? (
            getFilteredData(messages).map((message, index) => (
              <ExpandableCard 
                key={index} 
                item={message} 
                type="message"
                onStatusUpdate={(item, status) => handleStatusUpdate(item, status, 'message')}
              />
            ))
          ) : (
            <div className="text-center py-12 bg-white rounded-xl">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No contact messages found</p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default TravelInquiriesTab;
